import { Injectable, inject } from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {Store} from "@ngrx/store";
import {SharedPermissionsActions} from "@shared/stores/permissions/store/shared-permissions.actions-type";
import {AppPermissions, PermissionPackageDto, TokenDto} from "@server-models";
import {SharedIdentityApiService} from "@shared/stores/permissions/services/shared-identity-api.service";
import {TokenBaseHelperService} from "@shared/services/token-base-helper.service";
import {flatten, uniqBy} from "lodash";
import {TUserFeatureFlags} from "@shared/stores/permissions/store/shared-permissions.state";
import {EBetaFeatures} from "@shared/enums/beta-features.enum";
import {withLatestFrom} from "rxjs";
import {SharedLoginSelectors} from "@shared/components/login/store/shared-login.selector-type";

@Injectable({
  providedIn: 'root'
})
export class SharedPermissionsEffects {
  protected _store = inject(Store);
  protected actions$ = inject(Actions);
  protected _identityService = inject(SharedIdentityApiService);
  protected _tokenHelperService = inject(TokenBaseHelperService);


  getPermissions = createEffect(() => this.actions$.pipe(
    ofType(SharedPermissionsActions.getPermissions),
    withLatestFrom(this._store.select(SharedLoginSelectors.selectTenant)),
    mergeMap(([{ token }, tenant]) => {
      return this._identityService.getPackages().pipe(
        map((packages: PermissionPackageDto[]) => {
          return SharedPermissionsActions.getPermissionsSuccess({
            packages,
            userPermissions: this._getUserPermissions(packages, token),
            userFeatureFlags: this._getUserFeatureFlags(token, tenant?.tenantId!)
          });
        }),
        catchError((error) => [
          SharedPermissionsActions.getPermissionsFail({ error }),
        ])
      );
    })
  ));

  private _getUserPermissions(packages: PermissionPackageDto[], token: TokenDto): AppPermissions[] {
    const decodedToken = this._tokenHelperService.decodeToken(token?.token);
    let userPackages = JSON.parse(decodedToken.permissions);
    userPackages = flatten(userPackages.map((permission: any) => permission.permissions));

    return uniqBy(
      flatten(
        packages
          .filter(permissionPackage => userPackages.includes(permissionPackage.package))
          .map(permissionPackage => permissionPackage.permissions)
      ),
      permission => permission
    ) as AppPermissions[];
  }

  private _getUserFeatureFlags(tokenDto: TokenDto, tenantId: number): TUserFeatureFlags {
    const tenant = tokenDto.info?.tenants?.find(tenant => tenant.tenantId === tenantId);
    const featureFlags: string[] = tenant?.featureFlags ?? [];

    const userFeatureFlags: TUserFeatureFlags = {};

    for (const feature of featureFlags) {
      if ((Object.values(EBetaFeatures) as string[]).includes(feature)) {
        userFeatureFlags[feature as EBetaFeatures] = true;
      }
    }

    return userFeatureFlags;
  }
}


